import { aiActorTypeIds, type TicketCommentDTO } from "@zplanem/responses"
import type { Locale, Translations } from "@zplanem/translations"
import { type FormEvent, useMemo, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { MentionPicker } from "../../../components/chat/mention-picker"
import { MessageMarkdown } from "../../../components/chat/message-markdown"
import {
	type MentionCandidate,
	useMentionCompose,
} from "../../../components/chat/use-mention-compose"
import { ApiError } from "../../../lib/api-client"
import { formatFullDateTime, getDisplayName } from "../../../lib/user-display"
import { usePropertyMemberships } from "../../hooks/use-property-memberships"
import {
	useCreateTicketComment,
	useDeleteTicketComment,
	useTicketComments,
	useUpdateTicketComment,
} from "../../hooks/use-tickets"
import { TicketAttachments } from "./ticket-attachments"
import {
	buildTicketCommentMentionCandidates,
	canDeleteTicketComment,
	canEditTicketComment,
} from "./ticket-comments-model"
import {
	selectedMentionCandidatesFromMentions,
	ticketMentionRequestFields,
} from "./ticket-form-model"

type TicketCopy = Translations["tickets"]
type TicketMentionFields = ReturnType<typeof ticketMentionRequestFields>
const CONTROL_FOCUS =
	"focus:border-ring focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"

export function TicketComments({
	propertyId,
	ticketId,
	t,
	locale,
	viewerId,
	canManageTickets,
	ownedOrigins,
}: {
	propertyId: string
	ticketId: string
	t: TicketCopy
	locale: Locale
	viewerId: string | undefined
	canManageTickets: boolean
	ownedOrigins: readonly string[]
}) {
	const comments = useTicketComments(propertyId, ticketId)
	const createComment = useCreateTicketComment(propertyId, ticketId)
	const updateComment = useUpdateTicketComment(propertyId, ticketId)
	const deleteComment = useDeleteTicketComment(propertyId, ticketId)
	const [body, setBody] = useState("")
	const [error, setError] = useState<string | null>(null)
	const textareaRef = useRef<HTMLTextAreaElement | null>(null)
	const mentionListId = `ticket-comment-mentions-${ticketId}`
	const memberships = usePropertyMemberships(propertyId)
	const items = comments.data?.comments ?? []
	const mentionPeople = useMemo(
		() => buildTicketCommentMentionCandidates(memberships.data, viewerId),
		[memberships.data, viewerId],
	)
	const mention = useMentionCompose({
		people: mentionPeople,
		draft: body,
		setDraft: setBody,
		textareaRef,
	})

	async function submit(e: FormEvent) {
		e.preventDefault()
		const nextBody = body.trim()
		if (!nextBody) return
		const resolvedMentions = mention.resolveMentions(nextBody)
		setError(null)
		try {
			await createComment.mutateAsync({
				body: nextBody,
				...ticketMentionRequestFields(resolvedMentions),
			})
			setBody("")
			mention.reset()
		} catch (err) {
			setError(err instanceof ApiError ? err.message : t.commentCreateError)
		}
	}

	async function deleteOne(commentId: string) {
		setError(null)
		try {
			await deleteComment.mutateAsync(commentId)
		} catch (err) {
			setError(err instanceof ApiError ? err.message : t.commentDeleteError)
		}
	}

	async function editOne(
		commentId: string,
		nextBody: string,
		mentions: TicketMentionFields,
	): Promise<boolean> {
		const trimmed = nextBody.trim()
		if (!trimmed) return false
		setError(null)
		try {
			await updateComment.mutateAsync({
				commentId,
				body: { body: trimmed, ...mentions },
			})
			return true
		} catch (err) {
			setError(err instanceof ApiError ? err.message : t.commentEditError)
			return false
		}
	}

	return (
		<section className="mt-8 border-t border-border pt-6">
			<div className="flex items-center justify-between gap-3">
				<h2 className="text-sm font-semibold text-muted-foreground">
					{t.commentsTitle}
				</h2>
				<span className="text-xs text-muted-foreground">{items.length}</span>
			</div>

			<form onSubmit={submit} className="mt-3 space-y-2">
				<div
					role="combobox"
					aria-label={t.commentPlaceholder}
					aria-haspopup="listbox"
					aria-expanded={mention.open}
					aria-controls={mention.open ? mentionListId : undefined}
					aria-activedescendant={
						mention.open
							? `${mentionListId}-option-${mention.activeIndex}`
							: undefined
					}
					aria-autocomplete="list"
					className="relative"
					tabIndex={0}
				>
					{mention.open ? (
						<MentionPicker
							id={mentionListId}
							label={t.mentionSuggestionsLabel}
							candidates={mention.candidates}
							activeIndex={mention.activeIndex}
							onSelect={mention.select}
							onHover={mention.setActiveIndex}
						/>
					) : null}
					<textarea
						ref={textareaRef}
						value={body}
						onChange={mention.onChange}
						onKeyDown={mention.onKeyDown}
						onKeyUp={mention.onCaretMove}
						onClick={mention.onCaretMove}
						placeholder={t.commentPlaceholder}
						rows={3}
						className={`w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none disabled:opacity-60 ${CONTROL_FOCUS}`}
						disabled={createComment.isPending}
					/>
				</div>
				<div className="flex justify-end">
					<Button
						type="submit"
						size="sm"
						disabled={createComment.isPending || !body.trim()}
					>
						{createComment.isPending ? t.commentSubmitting : t.commentSubmit}
					</Button>
				</div>
			</form>

			{error ? (
				<div
					role="alert"
					className="mt-3 rounded-lg border border-destructive bg-destructive px-3 py-2 text-sm text-destructive-foreground"
				>
					{error}
				</div>
			) : null}

			{comments.isLoading ? (
				<div className="mt-4 text-sm text-muted-foreground">
					{t.loadingMore}
				</div>
			) : comments.error ? (
				<div
					role="alert"
					className="mt-4 rounded-lg border border-destructive bg-destructive px-3 py-3 text-sm text-destructive-foreground"
				>
					<div className="font-medium">{t.errorTitle}</div>
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={() => comments.refetch()}
						className="mt-2"
					>
						{t.errorRetry}
					</Button>
				</div>
			) : items.length === 0 ? (
				<p className="mt-4 text-sm text-muted-foreground">{t.commentsEmpty}</p>
			) : (
				<div className="mt-4 space-y-4">
					{items.map((comment) => (
						<TicketCommentItem
							key={comment.id}
							comment={comment}
							propertyId={propertyId}
							ticketId={ticketId}
							t={t}
							locale={locale}
							ownedOrigins={ownedOrigins}
							canDelete={canDeleteTicketComment(
								comment,
								viewerId,
								canManageTickets,
							)}
							canEdit={canEditTicketComment(comment, viewerId)}
							canDeleteAttachments={canManageTickets}
							viewerId={viewerId}
							deletePending={deleteComment.isPending}
							editPending={updateComment.isPending}
							mentionPeople={mentionPeople}
							onEdit={(body, mentions) => editOne(comment.id, body, mentions)}
							onDelete={() => {
								void deleteOne(comment.id)
							}}
						/>
					))}
				</div>
			)}
		</section>
	)
}

function TicketCommentItem({
	comment,
	propertyId,
	ticketId,
	t,
	locale,
	ownedOrigins,
	canDelete,
	canEdit,
	canDeleteAttachments,
	viewerId,
	deletePending,
	editPending,
	mentionPeople,
	onEdit,
	onDelete,
}: {
	comment: TicketCommentDTO
	propertyId: string
	ticketId: string
	t: TicketCopy
	locale: Locale
	ownedOrigins: readonly string[]
	canDelete: boolean
	canEdit: boolean
	canDeleteAttachments: boolean
	viewerId: string | undefined
	deletePending: boolean
	editPending: boolean
	mentionPeople: MentionCandidate[]
	onEdit: (body: string, mentions: TicketMentionFields) => Promise<boolean>
	onDelete: () => void
}) {
	const [isEditing, setIsEditing] = useState(false)
	const [draft, setDraft] = useState(comment.body)
	const editTextareaRef = useRef<HTMLTextAreaElement | null>(null)
	const editMentionListId = `ticket-comment-${comment.id}-mentions`
	const editMention = useMentionCompose({
		people: mentionPeople,
		draft,
		setDraft,
		textareaRef: editTextareaRef,
	})
	const authorLabel =
		comment.actorType === "ai" && comment.actorId === aiActorTypeIds.mareczekAi
			? "Mareczek"
			: comment.author
				? getDisplayName(comment.author)
				: "-"

	return (
		<article
			data-testid="ticket-comment"
			className="rounded-lg border border-border bg-muted/20 px-3 py-3"
		>
			<div className="flex flex-wrap items-start justify-between gap-2">
				<div className="min-w-0">
					<div className="text-sm font-medium text-foreground">
						{authorLabel}
					</div>
					<div className="mt-0.5 text-xs text-muted-foreground">
						{formatFullDateTime(comment.createdAt, locale)}
						{comment.editedAt ? ` - ${t.commentEdited}` : ""}
					</div>
				</div>
				<div className="flex items-center gap-1">
					{canEdit ? (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							disabled={editPending || deletePending}
							onClick={() => {
								setDraft(comment.body)
								editMention.setSelectedCandidates(
									selectedMentionCandidatesFromMentions(
										comment.mentions,
										mentionPeople,
									),
								)
								setIsEditing(true)
							}}
						>
							{t.commentEdit}
						</Button>
					) : null}
					{canDelete ? (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							disabled={deletePending || editPending}
							onClick={onDelete}
						>
							{t.commentDelete}
						</Button>
					) : null}
				</div>
			</div>

			{isEditing ? (
				<form
					className="mt-3 space-y-2"
					onSubmit={async (event) => {
						event.preventDefault()
						const trimmed = draft.trim()
						const saved = await onEdit(
							trimmed,
							ticketMentionRequestFields({
								...editMention.resolveMentions(trimmed),
								includeEmpty: true,
							}),
						)
						if (saved) {
							editMention.reset()
							setIsEditing(false)
						}
					}}
				>
					<div
						role="combobox"
						aria-label={t.commentEdit}
						aria-haspopup="listbox"
						aria-expanded={editMention.open}
						aria-controls={editMention.open ? editMentionListId : undefined}
						aria-activedescendant={
							editMention.open
								? `${editMentionListId}-option-${editMention.activeIndex}`
								: undefined
						}
						aria-autocomplete="list"
						className="relative"
						tabIndex={0}
					>
						{editMention.open ? (
							<MentionPicker
								id={editMentionListId}
								label={t.mentionSuggestionsLabel}
								candidates={editMention.candidates}
								activeIndex={editMention.activeIndex}
								onSelect={editMention.select}
								onHover={editMention.setActiveIndex}
							/>
						) : null}
						<textarea
							ref={editTextareaRef}
							value={draft}
							onChange={editMention.onChange}
							onKeyDown={editMention.onKeyDown}
							onKeyUp={editMention.onCaretMove}
							onClick={editMention.onCaretMove}
							rows={3}
							className={`w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none disabled:opacity-60 ${CONTROL_FOCUS}`}
							disabled={editPending}
						/>
					</div>
					<div className="flex justify-end gap-2">
						<Button
							type="button"
							variant="ghost"
							size="sm"
							disabled={editPending}
							onClick={() => {
								setDraft(comment.body)
								editMention.reset()
								setIsEditing(false)
							}}
						>
							{t.commentCancel}
						</Button>
						<Button
							type="submit"
							size="sm"
							disabled={editPending || !draft.trim()}
						>
							{t.commentSave}
						</Button>
					</div>
				</form>
			) : (
				<div className="mt-3 text-sm text-foreground">
					<MessageMarkdown ownedOrigins={ownedOrigins}>
						{comment.body}
					</MessageMarkdown>
				</div>
			)}

			<div className="mt-3">
				<TicketAttachments
					propertyId={propertyId}
					ticketId={ticketId}
					attachments={comment.attachments ?? []}
					t={t}
					target={{ commentId: comment.id }}
					canDelete={canDeleteAttachments}
					viewerId={viewerId}
					compact
				/>
			</div>
		</article>
	)
}
